/**
 * ChatWsClient - WebSocket transport abstraction for the AI chat session.
 *
 * Responsibilities:
 * - Open/close the WebSocket connection
 * - Authenticate using the Firebase ID token as a query param
 * - Dispatch strongly-typed ServerEvents to registered handlers
 * - Send ClientCommands as JSON
 * - Keep transport concerns separate from UI state
 *
 * See context/ai-chat.md §7 for the architecture reference.
 */

import type { ClientCommand, ServerEvent } from '@protoss/shared';
import { isServerEvent } from '@protoss/shared';

export type ChatWsEventHandler = (event: ServerEvent) => void;
export type ChatWsStatusHandler = (status: 'connected' | 'disconnected' | 'error') => void;

/**
 * App-level keepalive. The browser does not always surface a half-open socket (TCP gone
 * but readyState still OPEN), which would leave the UI looking connected while sends
 * silently vanish. We ping on an interval and expect a `pong` back within a short window;
 * a miss means the connection is dead and we force a reconnect.
 */
const PING_INTERVAL_MS = 25_000;
/** If a `pong` does not return within this window after a `ping`, treat the socket as dead. */
const PONG_TIMEOUT_MS = 10_000;

export interface ChatWsClientOptions {
  /** Base WS URL, e.g. ws://localhost:3001 */
  wsBaseUrl: string;
  /** Firebase ID token for authentication */
  idToken: string;
  /**
   * Optional session UUID - binds this WS connection to a specific DB chat session.
   * Passed as `?sessionUuid=<uuid>` in the connection URL so the server can resolve
   * `dbSessionId` eagerly at connect time instead of lazily on first `start_run`.
   */
  sessionUuid?: string;
  onEvent: ChatWsEventHandler;
  onStatus: ChatWsStatusHandler;
}

export class ChatWsClient {
  private ws: WebSocket | null = null;
  private opts: ChatWsClientOptions;
  private closed = false;
  /** Commands queued before the socket is OPEN - flushed in onopen. */
  private pendingQueue: ClientCommand[] = [];
  /** Keepalive ping interval - armed on open, cleared on close/disconnect. */
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  /** Dead-connection deadline armed after each ping; cleared when a pong arrives. */
  private pongTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(opts: ChatWsClientOptions) {
    this.opts = opts;
  }

  connect(): void {
    if (this.ws) return;

    const url = `${this.opts.wsBaseUrl}/ws/ai-chat${this.opts.sessionUuid ? `?sessionUuid=${encodeURIComponent(this.opts.sessionUuid)}` : ''}`;
    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      // Send auth frame first - token stays out of the URL so it is not
      // visible in browser DevTools network logs.
      this.ws?.send(JSON.stringify({ type: 'auth', token: this.opts.idToken }));
      // Flush commands that were queued while the socket was connecting.
      const queued = this.pendingQueue;
      this.pendingQueue = [];
      for (const cmd of queued) {
        this.ws?.send(JSON.stringify(cmd));
      }
      this.opts.onStatus('connected');
      this.startLiveness();
    };

    this.ws.onmessage = (evt: MessageEvent<string>) => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(evt.data);
      } catch {
        console.warn('[ChatWsClient] Non-JSON message received', evt.data);
        return;
      }
      // ANY inbound frame proves the socket is alive - cancel the pending dead-connection timer.
      // (Previously only a 'pong' reset it, so a long run that emitted other frames - or whose ping
      // reply was briefly delayed - could be mistaken for a dropped connection and trigger a false
      // "Reconnecting...". A server keepalive + run/tool frames now keep it alive.)
      this.clearPongTimer();
      const frameType = (parsed as { type?: unknown } | null)?.type;
      if (frameType === 'pong' || frameType === 'keepalive') {
        return; // pure liveness frame - not a run event
      }
      if (frameType === 'run_completed') {
        // A run just ended - a background job (e.g. corpus auto-tune) may have just started. Nudge an
        // immediate notifications refresh so the job toaster + bell reflect it now, not on the next poll.
        window.dispatchEvent(new Event('protoss:notifications-refresh'));
      }
      if (isServerEvent(parsed)) {
        this.opts.onEvent(parsed);
      } else {
        console.warn('[ChatWsClient] Unrecognized event shape', parsed);
      }
    };

    this.ws.onclose = (evt: CloseEvent) => {
      this.stopLiveness();
      this.ws = null;
      if (!this.closed) {
        // Treat auth rejection (4001) as a hard error so callers don't retry.
        if (evt.code === 4001) {
          console.error('[ChatWsClient] Auth rejected by server');
          this.opts.onStatus('error');
        } else {
          this.opts.onStatus('disconnected');
        }
      }
    };

    this.ws.onerror = () => {
      this.opts.onStatus('error');
    };
  }

  send(cmd: ClientCommand): void {
    if (this.closed) {
      console.warn('[ChatWsClient] Cannot send - client is closed', cmd.type);
      return;
    }
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(cmd));
    } else {
      // Queue until onopen fires
      this.pendingQueue.push(cmd);
    }
  }

  disconnect(): void {
    this.closed = true;
    this.stopLiveness();
    this.pendingQueue = []; // discard unsent commands
    if (this.ws) {
      this.ws.close(1000, 'Client closed');
      this.ws = null;
    }
  }

  get readyState(): number {
    return this.ws?.readyState ?? WebSocket.CLOSED;
  }

  // ── Keepalive / half-open detection ───────────────────────────────────────

  /** Begin the keepalive loop: ping on an interval, expect a pong each time. */
  private startLiveness(): void {
    this.stopLiveness();
    this.pingTimer = setInterval(() => this.sendPing(), PING_INTERVAL_MS);
  }

  /** Stop the keepalive loop and clear any pending dead-connection deadline. */
  private stopLiveness(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    this.clearPongTimer();
  }

  private clearPongTimer(): void {
    if (this.pongTimer) {
      clearTimeout(this.pongTimer);
      this.pongTimer = null;
    }
  }

  /** Send a keepalive ping and arm the dead-connection deadline (idempotent per round). */
  private sendPing(): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    if (this.pongTimer) return; // a ping is already outstanding - let its deadline decide
    const ping: ClientCommand = {
      type: 'ping',
      conversationId: '',
      ts: new Date().toISOString(),
    };
    this.ws.send(JSON.stringify(ping));
    this.pongTimer = setTimeout(() => {
      this.pongTimer = null;
      // No pong within the window: the socket is half-open (the browser still reports
      // OPEN). Force it closed so onclose fires and the caller's auto-reconnect runs.
      this.forceReconnect();
    }, PONG_TIMEOUT_MS);
  }

  /**
   * Drop a presumed-dead socket so the normal onclose → reconnect path runs. Unlike
   * disconnect(), this does NOT set `closed`, so the caller's auto-reconnect proceeds.
   */
  private forceReconnect(): void {
    this.stopLiveness();
    try {
      this.ws?.close();
    } catch {
      // Already closing/closed - onclose will still fire and drive reconnect.
    }
  }
}
